import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Package, Calendar, CreditCard, Hash, Mail } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_META = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700 border-blue-200', note: "We've received your order." },
  processing: { label: 'Processing', color: 'bg-amber-100 text-amber-700 border-amber-200', note: "Your order is being prepared." },
  shipped: { label: 'Shipped', color: 'bg-purple-100 text-purple-700 border-purple-200', note: "Your order is on its way." },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-700 border-green-200', note: "Your order has been delivered." },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700 border-red-200', note: "This order has been cancelled." },
};

const stageOf = (s) => STAGE_META[s] || STAGE_META.new;

const formatDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString() + ' ' + dt.toLocaleTimeString();
};

const OrderTracking = () => {
  const [params, setParams] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') || '');
  const [orderRef, setOrderRef] = useState(params.get('ref') || '');
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setLoading(true);
    try {
      const query = new URLSearchParams({ email });
      if (orderRef.trim()) query.set('order_ref', orderRef.trim());
      const res = await axios.get(`${BACKEND_URL}/api/orders/track?${query.toString()}`);
      setOrders(res.data.orders || []);
      setSearched(true);
      // Reflect current search params in URL so it is shareable / bookmarkable
      const next = new URLSearchParams();
      next.set('email', email);
      if (orderRef.trim()) next.set('ref', orderRef.trim());
      setParams(next, { replace: true });
      if ((res.data.orders || []).length === 0) {
        toast.info('No orders found for that email / reference.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to look up orders');
    } finally {
      setLoading(false);
    }
  };

  // Auto-run if both params already exist on mount
  React.useEffect(() => {
    if (params.get('email')) {
      runSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen pt-20 bg-gray-50" data-testid="order-tracking-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Track Your Order</h1>
          <p className="text-gray-600">
            Enter the email used at checkout, and optionally the last 8 characters of your Order #, to check status.
          </p>
        </div>

        <Card className="mb-8">
          <CardContent className="p-6">
            <form onSubmit={runSearch} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                  <Mail className="h-3 w-3" /> Email
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="order-tracking-email-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                  <Hash className="h-3 w-3" /> Order # <span className="text-gray-400 font-normal">(optional, last 8 chars)</span>
                </label>
                <Input
                  type="text"
                  value={orderRef}
                  onChange={(e) => setOrderRef(e.target.value)}
                  placeholder="e.g. A1B2C3D4"
                  maxLength={8}
                  data-testid="order-tracking-ref-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full md:w-auto text-white"
                  style={{ backgroundColor: '#00CED1' }}
                  data-testid="order-tracking-search-btn"
                >
                  <Search className="h-4 w-4 mr-2" />
                  {loading ? 'Searching…' : 'Track Order'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {searched && orders && orders.length === 0 && (
          <Card>
            <CardContent className="p-10 text-center text-gray-500" data-testid="order-tracking-empty">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="font-medium">No orders found</p>
              <p className="text-sm mt-1">
                Double-check the email address. If you still can't find it, contact us at{' '}
                <a href="mailto:sales@brainwave-group.com" className="underline" style={{ color: '#00CED1' }}>
                  sales@brainwave-group.com
                </a>.
              </p>
            </CardContent>
          </Card>
        )}

        {orders && orders.length > 0 && (
          <div className="space-y-4" data-testid="order-tracking-results">
            {orders.map((o) => {
              const meta = stageOf(o.stage);
              return (
                <Card key={o.order_ref + (o.createdAt || '')} data-testid={`order-tracking-row-${o.order_ref}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-lg font-bold">Order #{o.order_ref}</h3>
                          <Badge variant="outline" className={`capitalize ${meta.color}`}>
                            <Package className="h-3 w-3 mr-1" />
                            {meta.label}
                          </Badge>
                          {o.payment_status && (
                            <Badge className="bg-green-100 text-green-700 capitalize">
                              {o.payment_status}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{meta.note}</p>
                        <div className="flex flex-wrap gap-4 text-xs text-gray-500 mt-2">
                          <span className="flex items-center">
                            <Calendar className="h-3.5 w-3.5 mr-1" style={{ color: '#00CED1' }} />
                            Placed: {formatDate(o.createdAt)}
                          </span>
                          {o.stageUpdatedAt && (
                            <span className="flex items-center">
                              <Package className="h-3.5 w-3.5 mr-1" style={{ color: '#00CED1' }} />
                              Last update: {formatDate(o.stageUpdatedAt)}
                            </span>
                          )}
                          <span className="flex items-center">
                            <CreditCard className="h-3.5 w-3.5 mr-1" style={{ color: '#00CED1' }} />
                            {(o.currency || 'zar').toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <p className="text-2xl font-bold" style={{ color: '#00CED1' }}>
                        R{(o.amount || 0).toFixed(2)}
                      </p>
                    </div>

                    {o.items?.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <h4 className="text-xs font-semibold text-gray-700 mb-2">Items</h4>
                        <div className="space-y-2">
                          {o.items.map((it, i) => (
                            <div key={i} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                              <div className="flex items-center gap-3 min-w-0">
                                {it.image && (
                                  <img
                                    src={it.image}
                                    alt={it.name}
                                    className="w-10 h-10 object-cover rounded flex-shrink-0"
                                  />
                                )}
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate">{it.name}</p>
                                  <p className="text-xs text-gray-500">Qty: {it.quantity}</p>
                                </div>
                              </div>
                              <p className="text-sm font-semibold">R{((it.price || 0) * (it.quantity || 0)).toFixed(2)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;
